/**
 * aiReviewFailure — the ONE place the UI turns a failed run's `error` payload
 * into recruiter-facing prose (sub-task 03 R2.7, packet 3.2).
 *
 * A run can now die in two different places: scoring the candidates, or
 * building the derived file. Both land on `status = "failed"`; the only thing
 * that tells them apart is `error.stage`, which the apply worker stamps
 * `"apply"` (`ai_review_apply_worker/service.py`'s `_ERROR_STAGE`) and the
 * scoring worker leaves unset. That convention is the epic's OPEN DECISION E5
 * — provisional at the time of writing.
 *
 * Everything E5-dependent is therefore concentrated here: one constant, one
 * lookup, one fallback. If E5 is reversed, this module plus
 * `aiReviewFailure.unit.test.tsx` is the whole blast radius — no component
 * anywhere reads `error.stage`.
 *
 * The fallback is deliberately the GENERIC message, not the scoring one: an
 * absent or unrecognised stage means "we don't know", and the generic sentence
 * is true in either case.
 *
 * **The AI provider being unavailable is the one failure whose words are
 * written HERE rather than taken from `error.message`** (2026-09-29). Bedrock
 * answered ~every call with a 503 (errors observed 18:19-18:46 UTC), and
 * recruiters read the raw botocore text. The scoring worker now stamps a `code`
 * (`provider_unavailable`) and `retried_minutes`, and keeps the botocore text
 * in `message` for whoever reads the row. The split is deliberate: the WORKER
 * knows what happened and for how long, this module owns every sentence a
 * recruiter reads about a failed run — so a copy change never needs a worker
 * deploy, and no botocore string can reach the page through this code.
 *
 * While the worker is still waiting it writes `error.retrying` on a run that is
 * `processing`, and {@link aiReviewWaitingText} says so — the same field, so
 * the same module.
 */

/** `error.stage` value the apply worker stamps on a derived-file failure. */
const APPLY_STAGE = "apply";

/** `error.stage` value the SCORING worker stamps (`ai_review_worker`). */
const SCORING_STAGE = "scoring";

/** Headline shown when the derived file — not the scoring — is what failed.
 *  The scores survive and are still on screen, so retrying is free. */
const APPLY_HEADLINE = "The review couldn't be created.";

/** Headline when scoring itself failed. Distinct from the generic one because
 *  the recruiter's next move differs: there are no scores to keep, and
 *  retrying spends another round of model calls. */
const SCORING_HEADLINE = "Scoring the candidates failed.";

/** Headline when the stage is absent or unrecognised — i.e. genuinely unknown.
 *
 *  Both workers now stamp a stage (epic decision E5, option B), so absence no
 *  longer implies scoring. It means a pre-epic run from before the field
 *  existed, or a writer that forgot to stamp. Saying "AI Review failed" is true
 *  in every such case; naming a stage would be a guess. */
const GENERIC_HEADLINE = "AI Review failed to complete.";

/** Shown when the payload carries no usable `message`. */
const NO_DETAIL = "Please try again.";

/** `error.code` the scoring worker stamps when Bedrock would not answer for the
 *  whole retry budget (`ai_review_worker/domain.py` `PROVIDER_UNAVAILABLE`). */
const PROVIDER_UNAVAILABLE = "provider_unavailable";

/** `error.code` for a run that needed more worker invocations than the queue
 *  allows (`ai_review_worker/domain.py` `OUT_OF_TIME`, /pr-review #981 iter 2).
 *  The provider is not to blame, so the sentence must not say it is. */
const OUT_OF_TIME = "out_of_time";

/** Deliberately NOT "Click Retry to continue from where it stopped": a new
 *  review is a new run, and nothing resumes the old one's scores today — and
 *  the Import Detail page, where a full run fails, has no Retry at all. */
const OUT_OF_TIME_DETAIL =
  "This review needed more time than one run allows and stopped partway. Please start it again; if it stops again, split the people list into smaller ones.";

/** "The AI provider" rather than "Bedrock": the recruiter neither knows nor
 *  needs the vendor, and the sentence stays true if the provider changes. */
const PROVIDER_DOWN = "The AI provider is temporarily unavailable.";

/** Shown while the run waits between redeliveries. The second clause is the
 *  point: on 2026-09-29 two recruiters pressed Retry 29 times between them. */
const PROVIDER_WAITING = `${PROVIDER_DOWN} We'll keep retrying automatically, so there's no need to start over.`;

/** Shown while a PREVIEW is still queued (`pending`). With two runs scored at
 *  a time, a preview can wait minutes for a slot — longest exactly when the
 *  AI provider is struggling and every run is slow — and a Retry then only
 *  adds another run to the same queue (/pr-review #981 iter 1). */
const PREVIEW_QUEUED = "Waiting for a free slot to start scoring. It starts on its own, so there's no need to start over.";

/** `AiReviewResult.error` — an open JSONB blob on the wire. */
export type AiReviewErrorPayload = { [key: string]: unknown } | null | undefined;

/**
 * Full failure sentence for a failed run: which stage died, then why.
 *
 * @example
 *   aiReviewFailureText({ stage: "apply", message: "S3 unavailable." })
 *   // "The review couldn't be created. S3 unavailable."
 */
export function aiReviewFailureText(error: AiReviewErrorPayload): string {
  return `${headlineFor(error?.stage)} ${detailFor(error)}`;
}

/**
 * The "still waiting on the AI provider" sentence for a run that is
 * `processing` between redeliveries, or `null` when it is not waiting.
 *
 * Reads `error.retrying`, which the scoring worker writes only while the run is
 * `processing` and which the run's final write replaces — so a non-null answer
 * on a finished run cannot happen, and the caller still gates on the status.
 */
export function aiReviewWaitingText(error: AiReviewErrorPayload): string | null {
  const retrying = error?.retrying;
  if (typeof retrying !== "object" || retrying === null) return null;
  return PROVIDER_WAITING;
}

/**
 * The "queued, not failed" sentence for a preview that has not been picked up
 * yet, or `null` when it does not apply. Only previews: a full run's
 * recruiter has already been sent to the people list.
 */
export function aiReviewQueuedText(
  run: { status?: unknown; sample_size?: unknown } | undefined
): string | null {
  if (run?.status !== "pending" || run.sample_size == null) return null;
  return PREVIEW_QUEUED;
}

/** The second sentence: why. Only the provider-unavailable code has its own
 *  words; everything else is the worker's `message`, as before. */
function detailFor(error: AiReviewErrorPayload): string {
  if (error?.code === PROVIDER_UNAVAILABLE) {
    const minutes = error.retried_minutes;
    // No minutes means nothing was retried (a preview that failed on its first
    // single-attempt call, /pr-review #981 iter 2) — so no retry claim at all.
    if (typeof minutes === "number" && Number.isInteger(minutes) && minutes > 0) {
      return `${PROVIDER_DOWN} We retried for ${minutes} minute${minutes === 1 ? "" : "s"}; please try again later.`;
    }
    return `${PROVIDER_DOWN} Please try again in a few minutes.`;
  }
  if (error?.code === OUT_OF_TIME) return OUT_OF_TIME_DETAIL;
  return typeof error?.message === "string" ? error.message : NO_DETAIL;
}

/** The whole E5 surface: one lookup, three outcomes, unknown falls through. */
function headlineFor(stage: unknown): string {
  if (stage === APPLY_STAGE) return APPLY_HEADLINE;
  if (stage === SCORING_STAGE) return SCORING_HEADLINE;
  return GENERIC_HEADLINE;
}
